import { computed } from 'vue';
import * as z from 'zod/v4-mini';
import { userStorageKey } from '#layers/base/public';
import { useUserSessionRecord } from './use-user-session-record';

/**
 * Session-storage key for the popup checklist, keyed to the current user so its
 * progress can't bleed across a logout/login in the same tab.
 */
export function onboardingPopupKey(userId: string): string {
	return userStorageKey(userId, 'onboarding-popup');
}

const onboardingPopupStatuses = ['idle', 'active', 'dismissed', 'completed'] as const;

export const onboardingPopupTasks = [
	'createdWorkspace',
	'connectedDataSource',
	'managedSchema',
	'configuredPermissions',
	'connectedToStudio',
	'connectedAiAssistant',
	'invitedTeam',
] as const;

export type OnboardingPopupTask = (typeof onboardingPopupTasks)[number];

const onboardingPopupStateSchema = z.object({
	status: z.enum(onboardingPopupStatuses),
	collapsed: z.boolean(),
	stepsDone: z.array(z.enum(onboardingPopupTasks)),
});

type OnboardingPopupState = z.infer<typeof onboardingPopupStateSchema>;

interface OnboardingPopupTaskStatus {
	id: OnboardingPopupTask;
	done: boolean;
}

const DEFAULT_STATE: OnboardingPopupState = { status: 'idle', collapsed: false, stepsDone: [] };

export function useOnboardingPopup() {
	const { state, set, update } = useUserSessionRecord(
		onboardingPopupKey,
		onboardingPopupStateSchema,
		DEFAULT_STATE,
	);

	const totalCount = onboardingPopupTasks.length;

	const taskStatuses = computed<OnboardingPopupTaskStatus[]>(() => {
		return onboardingPopupTasks.map((id) => ({ id, done: state.value.stepsDone.includes(id) }));
	});

	const doneCount = computed(() => taskStatuses.value.filter((task) => task.done).length);

	const progress = computed(() => doneCount.value / totalCount);

	function start() {
		update((current) => (current.status === 'idle' ? { ...current, status: 'active' } : current));
	}

	function restart() {
		set({ ...DEFAULT_STATE, status: 'active' });
	}

	function markAsDone(task: OnboardingPopupTask) {
		update((current) => {
			if (current.stepsDone.includes(task)) return current;

			const stepsDone = [...current.stepsDone, task];

			return { ...current, stepsDone };
		});
	}

	function dismiss() {
		update((current) => {
			if (current.status === 'dismissed') {
				return current;
			}
			else {
				return { ...current, status: progress.value === 1 ? 'completed' : 'dismissed' };
			}
		});
	}

	function collapse() {
		update((current) => (current.collapsed ? current : { ...current, collapsed: true }));
	}

	function toggle() {
		update((current) => ({ ...current, collapsed: !current.collapsed }));
	}

	return {
		state,
		taskStatuses,
		doneCount,
		totalCount,
		progress,
		start,
		restart,
		markAsDone,
		dismiss,
		collapse,
		toggle,
	};
}
